/*
 * Free-trial countdown: tiers, time formatting and copy (169).
 *
 * Read by BOTH presentations — the fixed navbar chip and the dashboard's in-flow banner — so the two
 * cannot disagree about how much time is left or what to say about it.
 *
 * Nothing here ticks. The clock is useExpiryCountdown; these are pure functions of milliseconds, so
 * the copy for any given moment is reproducible without a timer.
 */

const ONE_MINUTE = 60 * 1000
const FIVE_MINUTES = 5 * ONE_MINUTE

/*
 * The three states the brief asks for. The thresholds are inclusive at the top -- at exactly five
 * minutes the student is in the five-minute tier, not the one above it.
 */
const TRIAL_TIER = {
	NORMAL: 'NORMAL',
	FIVE:   'FIVE',
	ONE:    'ONE',
}

const trialTier = (msLeft) => {
	if (msLeft <= ONE_MINUTE) return TRIAL_TIER.ONE
	if (msLeft <= FIVE_MINUTES) return TRIAL_TIER.FIVE
	return TRIAL_TIER.NORMAL
}

/*
 * "23 hours left" / "5 minutes left" / "45 seconds left".
 *
 * Singulars are spelled out: "1 minutes left" would land in the last minute of a trial, which is
 * precisely when someone is reading the words.
 *
 * fmtMinSec in prepkit-utils is deliberately left alone -- it is exported and used elsewhere, and
 * this is a copy decision for one feature, not a change to a shared formatter.
 */
const fmtTimeLeft = (millis) => {
	const totalSeconds = Math.max(0, Math.floor(millis / 1000))

	if (totalSeconds >= 3600) {
		const hours = Math.floor(totalSeconds / 3600)
		return `${hours} ${hours === 1 ? 'hour' : 'hours'} left`
	}
	if (totalSeconds >= 60) {
		const minutes = Math.floor(totalSeconds / 60)
		return `${minutes} ${minutes === 1 ? 'minute' : 'minutes'} left`
	}
	return `${totalSeconds} ${totalSeconds === 1 ? 'second' : 'seconds'} left`
}

/*
 * The same span without "left", for the "ends in ..." phrasing.
 *
 * It falls through to seconds on purpose: at thirty seconds the last tier reads "ends in 30 seconds"
 * rather than claiming a minute that has already gone.
 */
const fmtDuration = (millis) => {
	const totalSeconds = Math.max(0, Math.floor(millis / 1000))

	if (totalSeconds >= 3600) {
		const hours = Math.floor(totalSeconds / 3600)
		return `${hours} ${hours === 1 ? 'hour' : 'hours'}`
	}
	if (totalSeconds >= 60) {
		const minutes = Math.floor(totalSeconds / 60)
		return `${minutes} ${minutes === 1 ? 'minute' : 'minutes'}`
	}
	return `${totalSeconds} ${totalSeconds === 1 ? 'second' : 'seconds'}`
}

/*
 * The banner sentence, split so the time can be emphasised in the middle of it.
 *
 * Returns {lead, time, tail}: `time` is the coloured, bold part and the other two are plain. A
 * single interpolated string would have forced the caller to find the time again to style it.
 */
const trialMessage = (msLeft, isTrial = true) => {
	/*
	 * A paid kit runs out too, and says so differently: it is renewing access, not choosing a plan
	 * for the first time (193). Defaults to the trial wording so every existing caller is unchanged.
	 */
	if (!isTrial) {
		switch (trialTier(msLeft)) {
			case TRIAL_TIER.ONE:
				return {
					lead: 'Your access ends in ',
					time: fmtDuration(msLeft),
					tail: '. Your progress is saved. Renew now to carry on without a break.',
				}
			case TRIAL_TIER.FIVE:
				return {
					lead: 'Your access ends in ',
					time: fmtDuration(msLeft),
					tail: '. Your progress is saved. Renew now to keep studying.',
				}
			default:
				return {
					lead: 'You have ',
					time: fmtTimeLeft(msLeft),
					tail: ' on this PrepKit — renew to keep your access going.',
				}
		}
	}

	switch (trialTier(msLeft)) {
		case TRIAL_TIER.ONE:
			return {
				lead: 'Your trial ends in ',
				time: fmtDuration(msLeft),
				tail: ". Don't worry, your work is saved. Pick a plan now to continue right where you left off.",
			}
		case TRIAL_TIER.FIVE:
			return {
				lead: '',
				time: fmtTimeLeft(msLeft),
				tail: '. Your progress is saved. Pick a plan now to unlock continuous access and keep studying.',
			}
		default:
			return {
				lead: 'You have ',
				time: fmtTimeLeft(msLeft),
				tail: ' — pick a plan below to save your progress and keep going.',
			}
	}
}

export {TRIAL_TIER, ONE_MINUTE, FIVE_MINUTES, trialTier, fmtTimeLeft, fmtDuration, trialMessage}
